import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import type { RelationItem } from '../../features/admin/modules/modules.model';
import type { AutoFillResult } from '../services/cascading-auto-fill.service';

export interface AutoFillPendingItem {
  result: AutoFillResult;
  fieldLabel: string;
  needsConfirmation: boolean;
  oldDisplayValue: string;
  newDisplayValue: string;
}

export interface RelationSelectionGroup {
  moduleTitle: string;
  options: RelationOptionWithItems[];
}

export interface RelationOptionWithItems {
  relationItem: RelationItem;
  items: AutoFillPendingItem[];
}

export interface CombinedAutoFillDialogData {
  relationSelections: RelationSelectionGroup[];
  directItems: AutoFillPendingItem[];
}

@Component({
  selector: 'auto-fill-combined-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center gap-3 px-6 pb-1 pt-6">
      <mat-icon>sync_alt</mat-icon>
      <h2 class="text-lg font-semibold">Gegevens overnemen</h2>
    </div>

    <mat-dialog-content class="!px-6">
      @for (group of data.relationSelections; track $index; let groupIdx = $index) {
        <div class="mb-4">
          <p class="mb-3 text-sm">
            Er zijn meerdere gekoppelde {{ group.moduleTitle.toLowerCase() }} gevonden.
            Selecteer welke je wilt gebruiken:
          </p>
          @for (option of group.options; track option.relationItem.entry.id) {
            <button
              type="button"
              class="mb-2 flex w-full items-center gap-3 rounded border p-3 text-left transition-colors hover:bg-hover"
              [class]="isSelected(groupIdx, option)
                ? 'border-gray-400 dark:border-gray-500 bg-card'
                : 'border-gray-200 dark:border-gray-700'"
              (click)="selectOption(groupIdx, option)">
              <mat-icon>person</mat-icon>
              <span class="flex-1 font-medium">{{ option.relationItem.entry.selectedInput }}</span>
              @if (isSelected(groupIdx, option)) {
                <mat-icon class="text-green-600 dark:text-green-400">check_circle</mat-icon>
              }
            </button>
          }
        </div>
      }

      @if (overwriteChanges().length > 0) {
        <div class="mt-1 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950">
          <div class="flex items-center gap-2 border-b border-amber-200 px-4 py-2.5 dark:border-amber-800">
            <mat-icon class="text-amber-600 dark:text-amber-400" style="font-size: 20px; width: 20px; height: 20px">
              warning
            </mat-icon>
            <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
              @if (overwriteChanges().length === 1) {
                Dit veld is al ingevuld en wordt overschreven
              } @else {
                Deze velden zijn al ingevuld en worden overschreven
              }
            </span>
          </div>
          <div class="p-3">
            @for (change of overwriteChanges(); track $index) {
              <div class="rounded border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-default" [class.mb-2]="!$last">
                <div class="mb-1 text-sm font-medium">{{ change.fieldLabel }}</div>
                <div class="flex items-center gap-2 text-sm">
                  <span class="text-red-600 line-through dark:text-red-400">{{ change.oldDisplayValue }}</span>
                  <mat-icon class="text-gray-400 dark:text-gray-500" style="font-size: 16px; width: 16px; height: 16px">
                    arrow_forward
                  </mat-icon>
                  <span class="font-medium text-green-700 dark:text-green-400">{{ change.newDisplayValue }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close(undefined)">Annuleren</button>
      @if (overwriteChanges().length > 0) {
        <button mat-flat-button color="warn" (click)="applyAll()">Overschrijven</button>
      } @else {
        <button mat-flat-button color="primary" (click)="applyAll()">Toepassen</button>
      }
    </mat-dialog-actions>
  `,
  imports: [
    MatButtonModule,
    MatDialogContent,
    MatDialogActions,
    MatIconModule,
  ],
})
export class AutoFillCombinedDialogComponent {
  readonly data = inject<CombinedAutoFillDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<AutoFillCombinedDialogComponent>);

  readonly selectedOptions = signal<Record<number, RelationOptionWithItems>>({});

  constructor() {
    const initial: Record<number, RelationOptionWithItems> = {};
    this.data.relationSelections.forEach((group, idx) => {
      if (group.options.length > 0) {
        initial[idx] = group.options[0];
      }
    });
    this.selectedOptions.set(initial);
  }

  readonly allCurrentItems = computed(() => {
    const items: AutoFillPendingItem[] = [...this.data.directItems];
    const selected = this.selectedOptions();
    this.data.relationSelections.forEach((_, idx) => {
      const option = selected[idx];
      if (option) {
        items.push(...option.items);
      }
    });
    return items;
  });

  readonly overwriteChanges = computed(() =>
    this.allCurrentItems().filter(item => item.needsConfirmation)
  );

  isSelected(groupIdx: number, option: RelationOptionWithItems): boolean {
    return this.selectedOptions()[groupIdx]?.relationItem.entry.id === option.relationItem.entry.id;
  }

  selectOption(groupIdx: number, option: RelationOptionWithItems): void {
    this.selectedOptions.update(current => ({ ...current, [groupIdx]: option }));
  }

  applyAll(): void {
    this.dialogRef.close(this.allCurrentItems().map(item => item.result));
  }
}
